import React, { useState, useMemo, useEffect, useRef } from "react";
import { ArrowLeft, Calendar, Languages, X, ChevronDown } from "lucide-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Country, State } from "country-state-city";
import ISO6391 from "iso-639-1";
import styles from "./Step4_1MoreAboutYou.module.css";
import parentStyles from "../CreatorApplication.module.css";
import {
  formatDOBToDDMMYYYY,
  formatDOBToYYYYMMDD,
  getPickerValueFromBirthDate,
  calculateAge,
} from "../../MemberOnboarding/steps/Step4MemberProfile";
import { showToast } from "../../../../utils/toast";

interface Step4_1Props {
  onNext: (data?: any) => void;
  onBack: () => void;
  savedData?: any;
  canGoBack?: boolean;
}

const schema = yup.object({
  birthDate: yup
    .string()
    .required("Birth date is required")
    .test("is-18-years-old", "You must be at least 18 years old", function (value) {
      if (!value) return false;
      const age = calculateAge(value);
      if (age === null || isNaN(age)) {
        return this.createError({ message: "Please enter a valid birth date" });
      }
      if (age < 18) {
        return this.createError({ message: "You must be at least 18 years old" });
      }
      return true;
    }),
  gender: yup.string().required("Gender is required"),
  country: yup.string().required("Country is required"),
  state: yup.string().required("State is required"),
  city: yup.string().required("City is required"),
  hairColor: yup.string().required("Hair color is required"),
  bodyType: yup.string(),
  education: yup.string(),
  kids: yup.string(),
  ethnicity: yup.string(),
}).required();

const HAIR_COLOR_OPTIONS = [
  { value: "black", label: "Black" },
  { value: "brown", label: "Brown" },
  { value: "blonde", label: "Blonde" },
  { value: "red", label: "Red" },
  { value: "gray", label: "Gray" },
  { value: "white", label: "White" },
  { value: "auburn", label: "Auburn" },
  { value: "bald", label: "Bald" },
  { value: "other", label: "Other" },
];

type FormData = yup.InferType<typeof schema>;

export default function Step4_1MoreAboutYou({
  onNext,
  onBack,
  savedData,
  canGoBack = true,
}: Step4_1Props) {
  // 1. Languages setup (same logic as Step4_1MemberMoreAboutYou)
  const [languages, setLanguages] = useState<string[]>(() => {
    const initial = savedData?.language || savedData?.languages || ["English"];
    return Array.isArray(initial) ? initial.slice(0, 5) : ["English"];
  });
  const [langError, setLangError] = useState("");
  const [langSearch, setLangSearch] = useState("");
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  const allLanguages = useMemo(() => {
    return [...ISO6391.getAllNames()].sort((a, b) => a.localeCompare(b));
  }, []);

  const filteredLanguages = useMemo(() => {
    const query = langSearch.trim().toLowerCase();
    if (!query) return allLanguages;
    return allLanguages.filter((l) => l.toLowerCase().includes(query));
  }, [allLanguages, langSearch]);

  const handleSelectLanguage = (selectedLang: string) => {
    if (languages.includes(selectedLang)) {
      return;
    }
    if (languages.length >= 5) {
      setLangError("You can select up to 5 languages");
      return;
    }
    const updated = [...languages, selectedLang];
    setLanguages(updated);
    setLangSearch("");
    setLangError("");
    if (updated.length >= 5) {
      setIsLangDropdownOpen(false);
    }
  };

  const handleLangInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const firstAvailable = filteredLanguages.find((l) => !languages.includes(l));
      if (firstAvailable) {
        handleSelectLanguage(firstAvailable);
      }
    } else if (e.key === "Escape") {
      setIsLangDropdownOpen(false);
    }
  };

  const removeLanguage = (lang: string) => {
    setLanguages(languages.filter((l) => l !== lang));
    setLangError("");
  };

  // 2. Country & State setup (same logic as Step4_1MemberMoreAboutYou)
  const allCountries = useMemo(() => Country.getAllCountries(), []);

  const initialCountryName = useMemo(() => {
    const raw = savedData?.country || "";
    if (!raw) return "";
    const match = Country.getAllCountries().find(
      (c) => c.isoCode.toLowerCase() === raw.toLowerCase() || c.name.toLowerCase() === raw.toLowerCase()
    );
    return match ? match.name : raw;
  }, [savedData?.country]);

  const initialValState = useMemo(() => {
    const rawState = savedData?.state || "";
    if (!rawState) return "";
    const matchCountry = Country.getAllCountries().find(
      (c) =>
        c.isoCode.toLowerCase() === (savedData?.country || "").toLowerCase() ||
        c.name.toLowerCase() === (savedData?.country || "").toLowerCase()
    );
    if (!matchCountry) return rawState;
    const states = State.getStatesOfCountry(matchCountry.isoCode);
    const matchState = states.find(
      (s) => s.isoCode.toLowerCase() === rawState.toLowerCase() || s.name.toLowerCase() === rawState.toLowerCase()
    );
    return matchState ? matchState.name : rawState;
  }, [savedData?.country, savedData?.state]);

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      birthDate: formatDOBToDDMMYYYY(savedData?.birthDate || savedData?.DOB || ""),
      gender: savedData?.gender || "",
      country: initialCountryName,
      state: initialValState,
      city: savedData?.city || "",
      hairColor: savedData?.hairColor || "",
      bodyType: savedData?.bodyType || "",
      education: savedData?.education || savedData?.educationLevel || "",
      kids:
        savedData?.kids !== undefined
          ? String(savedData.kids)
          : savedData?.numberOfKids !== undefined
          ? String(savedData.numberOfKids)
          : "",
      ethnicity: Array.isArray(savedData?.ethnicity)
        ? savedData.ethnicity[0]
        : savedData?.ethnicity || "",
    },
  });

  const birthDateValue = watch("birthDate");
  const datePickerRef = useRef<HTMLInputElement>(null);

  const maxBirthDateForPicker = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d.toISOString().split("T")[0];
  }, []);

  const openDatePicker = () => {
    if (datePickerRef.current) {
      if (typeof datePickerRef.current.showPicker === "function") {
        try {
          datePickerRef.current.showPicker();
        } catch (e) {
          datePickerRef.current.focus();
        }
      } else {
        datePickerRef.current.focus();
      }
    }
  };

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value; // YYYY-MM-DD
    if (!val) return;
    const parts = val.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts;
      const formatted = `${day}/${month}/${year}`;
      setValue("birthDate", formatted, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const selectedCountry = watch("country");
  const selectedState = watch("state");

  const selectedCountryObj = useMemo(() => {
    if (!selectedCountry) return null;
    return (
      allCountries.find(
        (c) =>
          c.name.toLowerCase() === selectedCountry.toLowerCase() ||
          c.isoCode.toLowerCase() === selectedCountry.toLowerCase()
      ) || null
    );
  }, [allCountries, selectedCountry]);

  const availableStates = useMemo(() => {
    if (!selectedCountryObj) return [];
    return State.getStatesOfCountry(selectedCountryObj.isoCode);
  }, [selectedCountryObj]);

  const prevCountryRef = useRef(selectedCountry);

  const [countrySearch, setCountrySearch] = useState(initialCountryName || "");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  const [stateSearch, setStateSearch] = useState(initialValState || "");
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const stateDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (prevCountryRef.current !== selectedCountry) {
      prevCountryRef.current = selectedCountry;
      setValue("state", "");
      clearErrors("state");
      setStateSearch("");
    }
    setCountrySearch(selectedCountry || "");
  }, [selectedCountry, setValue, clearErrors]);

  useEffect(() => {
    setStateSearch(selectedState || "");
  }, [selectedState]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target as Node)) {
        setIsLangDropdownOpen(false);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target as Node)) {
        setIsCountryDropdownOpen(false);
      }
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(e.target as Node)) {
        setIsStateDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCountries = useMemo(() => {
    const q = countrySearch.trim().toLowerCase();
    if (!q || (selectedCountry && q === selectedCountry.toLowerCase())) {
      return allCountries;
    }
    return allCountries.filter((c) => c.name.toLowerCase().includes(q));
  }, [allCountries, countrySearch, selectedCountry]);

  const handleSelectCountry = (countryName: string) => {
    setValue("country", countryName, { shouldValidate: true });
    clearErrors("country");
    setCountrySearch(countryName);
    setIsCountryDropdownOpen(false);
  };

  const handleCountryInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredCountries.length > 0) {
        handleSelectCountry(filteredCountries[0].name);
      }
    } else if (e.key === "Escape") {
      setIsCountryDropdownOpen(false);
      setCountrySearch(selectedCountry || "");
    }
  };

  const filteredStates = useMemo(() => {
    const q = stateSearch.trim().toLowerCase();
    if (!q || (selectedState && q === selectedState.toLowerCase())) {
      return availableStates;
    }
    return availableStates.filter((s) => s.name.toLowerCase().includes(q));
  }, [availableStates, stateSearch, selectedState]);

  const handleSelectState = (stateName: string) => {
    setValue("state", stateName, { shouldValidate: true });
    clearErrors("state");
    setStateSearch(stateName);
    setIsStateDropdownOpen(false);
  };

  const handleStateInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredStates.length > 0) {
        handleSelectState(filteredStates[0].name);
      }
    } else if (e.key === "Escape") {
      setIsStateDropdownOpen(false);
      setStateSearch(selectedState || "");
    }
  };

  const onSubmit = (data: FormData) => {
    const payload = {
      ...savedData,
      headline: savedData?.headline?.trim() || "",
      avatar: savedData?.avatar,
      firstName: savedData?.firstName,
      lastName: savedData?.lastName,
      username: savedData?.username,
      aboutMe: savedData?.aboutMe,
      lookingFor: savedData?.lookingFor,
      whatAreYouLookingFor: savedData?.whatAreYouLookingFor,
      birthDate: data.birthDate?.trim(),
      DOB: formatDOBToYYYYMMDD(data.birthDate),
      gender: data.gender,
      country: data.country,
      state: data.state,
      city: data.city?.trim(),
      hairColor: data.hairColor?.trim() || "",
      bodyType: data.bodyType || "",
      education: data.education,
      educationLevel: data.education,
      numberOfKids: data.kids ? (data.kids === "3+" ? 3 : Number(data.kids) || data.kids) : 0,
      kids: data.kids,
      ethnicity: data.ethnicity ? [data.ethnicity] : [],
      ethnicities: data.ethnicity ? [data.ethnicity] : [],
      language: languages,
      languages: languages,
    };
    onNext(payload);
  };

  return (
    <div className={parentStyles.formCard}>
      <h1 className={styles.formTitle}>More About You</h1>
      <p className={styles.formSubtitle}>
        This is what your audience will see. You can update it at any time from your dashboard.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.formGrid}>
          {/* Birth Date */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>
              Birth date <span>*</span>
            </label>
            <div className={styles.inputWrapper}>
              <div
                className={styles.calendarIconClickable}
                onClick={openDatePicker}
                title="Open calendar"
              >
                <Calendar size={18} />
              </div>
              <input
                type="text"
                placeholder="dd/mm/yyyy"
                className={styles.inputWithIcon}
                {...register("birthDate")}
                onClick={openDatePicker}
              />
              <input
                ref={datePickerRef}
                type="date"
                tabIndex={-1}
                aria-hidden="true"
                value={getPickerValueFromBirthDate(birthDateValue || "")}
                max={maxBirthDateForPicker}
                onChange={handleDatePickerChange}
                className={styles.hiddenDatePicker}
              />
            </div>
            {errors.birthDate && <p className={parentStyles.errorText}>{errors.birthDate.message}</p>}
          </div>

          {/* Gender */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>
              Gender <span>*</span>
            </label>
            <select className={styles.select} {...register("gender")} defaultValue="" required>
              <option value="" disabled>
                Select gender
              </option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="non-binary">Non-binary</option>
            </select>
            {errors.gender && <p className={parentStyles.errorText}>{errors.gender.message}</p>}
          </div>

          {/* Country (Searchable dropdown with flag & autocomplete matching Member onboarding) */}
          <input type="hidden" {...register("country")} />
          <div className={styles.inputGroup}>
            <label className={styles.label}>
              Country <span>*</span>
            </label>
            <div className={styles.inputWrapper} ref={countryDropdownRef}>
              <input
                type="text"
                placeholder="Select country"
                className={styles.inputSearchableSingle}
                value={countrySearch}
                onChange={(e) => {
                  setCountrySearch(e.target.value);
                  setIsCountryDropdownOpen(true);
                  if (errors.country) clearErrors("country");
                }}
                onFocus={(e) => {
                  setIsCountryDropdownOpen(true);
                  e.target.select();
                }}
                onKeyDown={handleCountryInputKeyDown}
              />
              <button
                type="button"
                className={styles.chevronButton}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsCountryDropdownOpen((prev) => !prev);
                }}
                tabIndex={-1}
                aria-label="Toggle country dropdown"
              >
                <ChevronDown
                  size={16}
                  className={`${styles.inputChevron} ${
                    isCountryDropdownOpen ? styles.inputChevronOpen : ""
                  }`}
                />
              </button>

              {isCountryDropdownOpen && (
                <div className={styles.dropdownList}>
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((c) => {
                      const isSelected = selectedCountry === c.name;
                      return (
                        <div
                          key={c.isoCode}
                          className={`${styles.dropdownItem} ${
                            isSelected ? styles.dropdownItemSelected : ""
                          }`}
                          onClick={() => handleSelectCountry(c.name)}
                        >
                          <span>{c.name}</span>
                          {isSelected && (
                            <span style={{ marginLeft: "auto", fontSize: 13, opacity: 0.8 }}>
                              ✓
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.dropdownEmpty}>No countries found</div>
                  )}
                </div>
              )}
            </div>
            {errors.country && <p className={parentStyles.errorText}>{errors.country.message}</p>}
          </div>

          {/* State / Province (Dynamic states based on Country) */}
          <input type="hidden" {...register("state")} />
          <div className={styles.inputGroup}>
            <label className={styles.label}>
              State/Province <span>*</span>
            </label>
            {availableStates.length > 0 ? (
              <div className={styles.inputWrapper} ref={stateDropdownRef}>
                <input
                  type="text"
                  placeholder="Select state/province"
                  className={styles.inputSearchableSingle}
                  value={stateSearch}
                  onChange={(e) => {
                    setStateSearch(e.target.value);
                    setIsStateDropdownOpen(true);
                    if (errors.state) clearErrors("state");
                  }}
                  onFocus={(e) => {
                    setIsStateDropdownOpen(true);
                    e.target.select();
                  }}
                  onKeyDown={handleStateInputKeyDown}
                />
                <button
                  type="button"
                  className={styles.chevronButton}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsStateDropdownOpen((prev) => !prev);
                  }}
                  tabIndex={-1}
                  aria-label="Toggle state dropdown"
                >
                  <ChevronDown
                    size={16}
                    className={`${styles.inputChevron} ${
                      isStateDropdownOpen ? styles.inputChevronOpen : ""
                    }`}
                  />
                </button>

                {isStateDropdownOpen && (
                  <div className={styles.dropdownList}>
                    {filteredStates.length > 0 ? (
                      filteredStates.map((st) => {
                        const isSelected = selectedState === st.name;
                        return (
                          <div
                            key={`${st.countryCode}-${st.isoCode || st.name}`}
                            className={`${styles.dropdownItem} ${
                              isSelected ? styles.dropdownItemSelected : ""
                            }`}
                            onClick={() => handleSelectState(st.name)}
                          >
                            <span>{st.name}</span>
                            {isSelected && (
                              <span style={{ marginLeft: "auto", fontSize: 13, opacity: 0.8 }}>
                                ✓
                              </span>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className={styles.dropdownEmpty}>No states found</div>
                    )}
                  </div>
                )}
              </div>
            ) : selectedCountryObj ? (
              <input
                type="text"
                placeholder="Enter state/province"
                className={styles.input}
                {...register("state", {
                  onChange: (e) => {
                    if (e.target.value) clearErrors("state");
                  },
                })}
              />
            ) : (
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  placeholder="Select country first"
                  className={styles.inputSearchableSingle}
                  disabled
                  value=""
                />
                <button
                  type="button"
                  className={styles.chevronButton}
                  disabled
                  tabIndex={-1}
                  style={{ opacity: 0.5, cursor: "not-allowed" }}
                >
                  <ChevronDown size={16} className={styles.inputChevron} />
                </button>
              </div>
            )}
            {errors.state && <p className={parentStyles.errorText}>{errors.state.message}</p>}
          </div>

          {/* City */}
          <div className={styles.inputGroupFull}>
            <label className={styles.label}>
              City <span>*</span>
            </label>
            <input
              type="text"
              placeholder="City of residence"
              className={styles.input}
              {...register("city")}
            />
            {errors.city && <p className={parentStyles.errorText}>{errors.city.message}</p>}
          </div>

          {/* Hair Color */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>
              Hair color <span>*</span>
            </label>
            <select className={styles.select} {...register("hairColor")} defaultValue="" required>
              <option value="" disabled>
                Select hair color
              </option>
              {HAIR_COLOR_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.hairColor && <p className={parentStyles.errorText}>{errors.hairColor.message}</p>}
          </div>

          {/* Body Type (Options: slim, athletic, average, plus) */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>Body type</label>
            <select className={styles.select} {...register("bodyType")} defaultValue="">
              <option value="">Select body type</option>
              <option value="slim">Slim</option>
              <option value="athletic">Athletic</option>
              <option value="average">Average</option>
              <option value="plus">Plus</option>
            </select>
          </div>

          {/* Education Level */}
          <div className={styles.inputGroupFull}>
            <label className={styles.label}>Education level</label>
            <select className={styles.select} {...register("education")} defaultValue="">
              <option value="">Choose your qualification level</option>
              <option value="highschool">High School</option>
              <option value="bachelors">Bachelor's Degree</option>
              <option value="masters">Master's Degree</option>
              <option value="phd">PhD</option>
            </select>
          </div>

          {/* Number of Kids */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>Number of kids</label>
            <select className={styles.select} {...register("kids")} defaultValue="">
              <option value="">Select</option>
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3+">3+</option>
            </select>
          </div>

          {/* Ethnicity */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>Ethnicity</label>
            <select className={styles.select} {...register("ethnicity")} defaultValue="">
              <option value="">Select your ethnicity</option>
              <option value="asian">Asian</option>
              <option value="black">Black</option>
              <option value="hispanic">Hispanic/Latino</option>
              <option value="white">White</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Language (Searchable multi-select matching Member onboarding) */}
          <div className={styles.inputGroupFull}>
            <label className={styles.label}>
              Language <span>*</span>
            </label>
            <div className={styles.inputWrapper} ref={langDropdownRef}>
              <Languages size={18} className={styles.inputIcon} />
              <input
                type="text"
                placeholder={
                  languages.length >= 5 ? "Maximum 5 languages selected" : "You Speak in"
                }
                className={styles.inputWithIcon}
                value={langSearch}
                onChange={(e) => {
                  setLangSearch(e.target.value);
                  setIsLangDropdownOpen(true);
                  if (langError) setLangError("");
                }}
                onFocus={() => {
                  if (languages.length < 5) {
                    setIsLangDropdownOpen(true);
                  }
                }}
                onKeyDown={handleLangInputKeyDown}
                disabled={languages.length >= 5}
              />
              <button
                type="button"
                className={styles.chevronButton}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (languages.length < 5) {
                    setIsLangDropdownOpen((prev) => !prev);
                  }
                }}
                tabIndex={-1}
                aria-label="Toggle language dropdown"
              >
                <ChevronDown
                  size={16}
                  className={`${styles.inputChevron} ${
                    isLangDropdownOpen ? styles.inputChevronOpen : ""
                  }`}
                />
              </button>

              {isLangDropdownOpen && languages.length < 5 && (
                <div className={styles.dropdownList}>
                  {filteredLanguages.length > 0 ? (
                    filteredLanguages.map((lang) => {
                      const isSelected = languages.includes(lang);
                      return (
                        <div
                          key={lang}
                          className={`${styles.dropdownItem} ${
                            isSelected ? styles.dropdownItemSelected : ""
                          }`}
                          onClick={() => {
                            if (!isSelected) {
                              handleSelectLanguage(lang);
                            }
                          }}
                        >
                          <span>{lang}</span>
                          {isSelected && (
                            <span style={{ marginLeft: "auto", fontSize: 13, opacity: 0.8 }}>
                              ✓ Added
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.dropdownEmpty}>No languages found</div>
                  )}
                </div>
              )}
            </div>
            {langError && <p className={parentStyles.errorText}>{langError}</p>}

            {languages.length > 0 && (
              <div className={styles.tagsContainer}>
                {languages.map((lang) => (
                  <div key={lang} className={styles.tag}>
                    {lang}
                    <button
                      type="button"
                      onClick={() => removeLanguage(lang)}
                      title={`Remove ${lang}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => {
              if (!canGoBack) {
                showToast("Back not allowed", "info");
                return;
              }
              onBack();
            }}
            title={!canGoBack ? "Back not allowed" : undefined}
            style={!canGoBack ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            <ArrowLeft size={18} /> Back
          </button>
          <button type="submit" className={styles.continueBtn}>
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
